import React, { useState, useEffect, useRef } from "react";
import languageChat from "assets/images/languageChat.png";
import fileUploadChat from "assets/images/fileUploadChat.png";
import {
  CarouselContainer,
  CarouselImage,
  CarouselImagesContainer,
  CarouselImagesOuterContainer,
  CarouselWrapper,
  Dot,
  DotsContainer,
} from "./carousel.styles";
import { MessageRow } from "modules/chat/components/messages/message";
import { MessageType } from "modules/chat/components/chat.type";

// Define messages
const images = [languageChat, fileUploadChat];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const Carousel = () => {
  const [currentName, setCurrentName] = useState<number>(0); // Current conversation index
  const [visibleMessages, setVisibleMessages] = useState<{ [key: string]: MessageType[] }>({});
  const totalImages = images.length;
  const divRef: any = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (divRef.current) {
      setContainerWidth(divRef.current.offsetWidth);
    }
  }, []);

  const user = {
    sub: "string",
    given_name: "string",
    family_name: "string",
    email_verified: true,
    preferred_username: "string",
    email: "91af764e8e04e4f950f5db56fb0bacbd6e43e4",
  };

  const now = new Date().toISOString();
  const message3 = {
    chatid: 1,
    _id: 1,
    loading: true,
    role: "user",
    content: 'Can you please describe to me the code snippet I uploaded earlier?',
    createdAt: now,
    updatedAt: now
  };

  const message4 = {
    chatid: 2,
    _id: 2,
    loading: true,
    role: "assistant",
    content: '## Functionality:\n**Random String Generation:** The `generate_random_string` function creates random alphanumeric strings of a specified length (defaulting to 12), used for `conversationid` and `chatid`.\n   ```python\n   def generate_random_string(length=12):\n       """Generate a random string of fixed length."""\n       letters = string.ascii_letters + string.digits\n       return "".join(random.choice(letters) for i in range(length))\n```',
    createdAt: now,
    updatedAt: now
  };

  const message5 = {
    chatid: 3,
    _id: 3,
    loading: true,
    role: "user",
    content: "please elaborate add some type checking to this",
    createdAt: now,
    title: now,
  };

  const now4 = new Date().toISOString();
  const message1 = {
    chatid: 4,
    _id: 4,
    loading: true,
    role: "user",
    content: 'File upload 92b7c8d9e7f5c1a6d2541d3fe0568bcd2575b2b45 {"name":"test4.py","hash":"92b7c8d9e7f5c1a6d2541d3fe0568bcd2575b2b45c34","type":"text/x-python-script","size":2500,"position":0,"totalchunks":1}',
    createdAt: now,
    updatedAt: now
  };

  const now5 = new Date().toISOString();
  const message2 = {
    chatid: 5,
    _id: 5,
    loading: true,
    role: "assistant",
    content: '#### Code Explanation:\n**File Handling:** It reads the file in chunks to avoid memory overload. The file is opened using Python\'s `open()` function, and it processes each chunk sequentially.\n\n **Hashing:** The `hashlib` library is used to generate a hash for the file, ensuring its integrity during the upload process. The hash is calculated in a loop, iterating over each chunk of the file.\n   ```python\n   def calculate_file_hash(file_path):\n       """Calculate the hash of a file using SHA-256."""\n       hash_sha256 = hashlib.sha256()\n       with open(file_path, \'rb\') as f:\n           while chunk := f.read(4096):\n               hash_sha256.update(chunk)\n       return hash_sha256.hexdigest()\n   ```\n',
    createdAt: now,
    updatedAt: now
  };

  const conversations: any = [
    { messageExample2: [message3, message4, message5] },
    { messageExample1: [message1, message2] },
  ];

  // Automatically add messages with fade-in effect
  useEffect(() => {
    const addMessage = async (message, index) => {
      if (message.user === "assistant") {
        setVisibleMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages };
          updatedMessages[currentName] = [
            ...(updatedMessages[currentName] || []),
            { ...message, loading: true, isMessageVisible: false }, // Initially set visibility to false
          ];
          return updatedMessages;
        });

        await sleep(2000);

        setVisibleMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages };
          updatedMessages[currentName] = updatedMessages[currentName].map((m) =>
            m._id === message._id
              ? { ...m, loading: false, isMessageVisible: true }
              : m
          );
          return updatedMessages;
        });
      } else {
        if (index > 1) {
          await sleep(2000);
        }

        setVisibleMessages((prevMessages) => {
          const updatedMessages = { ...prevMessages };
          updatedMessages[currentName] = [
            ...(updatedMessages[currentName] || []),
            { ...message, loading: false, isMessageVisible: true },
          ];
          return updatedMessages;
        });
      }
    };
    
    if (conversations[currentName].length > 0) {
      conversations[currentName][Object.keys(conversations[currentName])[0]].forEach((message, index) => {
        setTimeout(() => {
          addMessage(message, index);
        }, (index + 1) * 2000);
      });
    } else {
      setVisibleMessages((prevMessages) => {
        const updatedMessages = { ...prevMessages };
        updatedMessages[currentName] = [];
        return updatedMessages;
      });

      conversations[currentName][Object.keys(conversations[currentName])[0]].forEach((message, index) => {
        setTimeout(() => {
          addMessage(message, index);
        }, (index + 1) * 2000);
      });
    }
  }, [currentName]);

  const handleDotClick = (index: number) => {
    setCurrentName(index);
  };

  return (
    <CarouselContainer>
      <div style={{ minHeight: "75%", height: "min-content", alignSelf: "center" }}>
        {visibleMessages[currentName] ? visibleMessages[currentName].map((message, index) => (
          <div key={message._id} ref={divRef}>
            {message.loading && <div className="mt-3 mb-3"></div>}
            <div
              className="mt-3 mb-2 grid"
              style={{
                opacity: message.loading ? 1 : 0,
                display: message.loading ? "flex" : "none",
                minHeight: message.loading ? "150px" : "0px",
                height: message.loading ? "150px" : "0px",
                transition: "opacity 1s ease-in",
                alignItems: "center",
                marginTop: "2rem",
                marginBottom: "2rem",
              }}
            >
              <MessageRow
                amsUser={user}
                message={message}
                userPrompt={""}
                loading={true}
                conversationId={"login-screen"}
                disableFeedback={true}
                containerWidth={containerWidth}
              />
            </div>
            <div
              className="mt-3 mb-2 grid"
              style={{
                opacity: message.loading ? 0 : 1, 
                height: message.loading ? "0px" : "max-content", 
                transition: "opacity 1s ease-in",
                marginTop: "2rem",
                marginBottom: "2rem",
              }}
            >
              <MessageRow
                amsUser={user}
                message={message}
                userPrompt={""}
                loading={false}
                conversationId={"login-screen"}
                disableFeedback={true}
                containerWidth={containerWidth}
              />
            </div>
          </div>
        )) : null}
      </div>
      {/* <DotsContainer>
        {conversations.map((_, index) => (
          <Dot
            key={index}
            onClick={() => handleDotClick(index)}
            style={{
              backgroundColor: currentName === index ? "black" : "#bbb",
            }}
          ></Dot>
        ))}
      </DotsContainer> */}
    </CarouselContainer>
  );
};

export default Carousel;
